import { logger } from '@magicschool/logger';
import { dataOnSuccess } from '@magicschool/supabase/utils';
import { handle } from 'features/db/error';
import { clientSupabase } from 'util/supabase';
import type { UserRole } from '../server/actions/rbac/types';
import type { FeedbackData, FeedbackType, Message, MessageFeedback } from './types';

type GenerateMessageSuggestionsParams = {
  context: Message[];
  role: UserRole;
  locale?: string;
  toolSlug: string;
};

export async function generateMessageSuggestions(params: GenerateMessageSuggestionsParams) {
  const { context, role, locale, toolSlug } = params;
  const response = await fetch('/api/generations/generate-message-suggestions', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ messages: context, role, locale, toolSlug }),
  });

  if (!response.ok) {
    logger.error('Failed to generate message suggestions', response);
    return [];
  }

  const suggestions: string[] = await response.json();
  return suggestions;
}

export function saveUserAssistantThreadMessageFeedback(
  userId: string,
  messageId: number,
  feedbackType: FeedbackType,
  feedbackData: FeedbackData,
) {
  return clientSupabase.from('user_assistant_thread_message_feedback').upsert(
    { user_id: userId, assistant_thread_message_id: messageId, feedback_type: feedbackType, feedback_data: feedbackData },
    {
      onConflict: 'user_id, assistant_thread_message_id, feedback_type',
    },
  );
}

export function saveRoomSummaryFeedback(
  userId: string,
  roomStudentSummaryId: number,
  feedbackType: FeedbackType,
  feedbackData: FeedbackData,
) {
  return clientSupabase.from('user_room_student_summary_feedback').upsert(
    { user_id: userId, room_student_summary_id: roomStudentSummaryId, feedback_type: feedbackType, feedback_data: feedbackData },
    {
      onConflict: 'room_student_summary_id, user_id',
    },
  );
}

export function saveAssistantThreadSummaryFeedback(
  userId: string,
  assistantThreadSummaryId: number,
  feedbackType: FeedbackType,
  feedbackData: FeedbackData,
) {
  return clientSupabase.from('user_assistant_thread_summary_feedback').upsert(
    { user_id: userId, assistant_thread_summary_id: assistantThreadSummaryId, feedback_type: feedbackType, feedback_data: feedbackData },
    {
      onConflict: 'assistant_thread_summary_id, user_id',
    },
  );
}

export function loadUserAssistantThreadMessageFeedback(userId: string, summaryId: number) {
  return clientSupabase
    .from('user_assistant_thread_message_feedback')
    .select()
    .eq('user_id', userId)
    .eq('assistant_thread_message_id', summaryId)
    .maybeSingle()
    .then(dataOnSuccess);
}
export function loadRoomSummaryFeedback(userId: string, summaryId: number) {
  return clientSupabase
    .from('user_room_student_summary_feedback')
    .select()
    .eq('user_id', userId)
    .eq('room_student_summary_id', summaryId)
    .maybeSingle()
    .then(dataOnSuccess);
}
export function loadAssistantThreadSummaryFeedback(userId: string, messageId: number) {
  return clientSupabase
    .from('user_assistant_thread_summary_feedback')
    .select()
    .eq('user_id', userId)
    .eq('assistant_thread_summary_id', messageId)
    .maybeSingle()
    .then(dataOnSuccess);
}

export function removeMessageFeedback(userId: string, messageId: number, feedbackType: FeedbackType) {
  return clientSupabase
    .from('user_assistant_thread_message_feedback')
    .delete()
    .eq('user_id', userId)
    .eq('assistant_thread_message_id', messageId)
    .eq('feedback_type', feedbackType)
    .then(handle);
}

export async function getUserMessageFeedback(userId: string): Promise<MessageFeedback[]> {
  return await clientSupabase.from('user_assistant_thread_message_feedback').select().eq('user_id', userId).then(handle);
}

export function saveUserQuizFeedback(userId: string, quizId: string, feedbackType: FeedbackType, feedbackData: FeedbackData) {
  return clientSupabase.from('user_quiz_feedback').upsert(
    { user_id: userId, quiz_id: quizId, feedback_type: feedbackType, feedback_data: feedbackData },
    {
      onConflict: 'user_id, quiz_id, feedback_type',
    },
  );
}

export function saveUserQuizQuestionFeedback(
  userId: string,
  quizAttemptId: string,
  quizVersionQuestionId: string,
  feedbackType: FeedbackType,
  feedbackData: FeedbackData,
) {
  return clientSupabase.from('user_quiz_question_feedback').upsert(
    {
      user_id: userId,
      quiz_attempt_id: quizAttemptId,
      quiz_version_question_id: quizVersionQuestionId,
      feedback_type: feedbackType,
      feedback_data: feedbackData,
    },
    {
      onConflict: 'user_id, quiz_attempt_id, quiz_version_question_id',
    },
  );
}

export function loadUserQuizFeedback(userId: string, quizId: string) {
  return clientSupabase.from('user_quiz_feedback').select().eq('user_id', userId).eq('quiz_id', quizId).maybeSingle().then(dataOnSuccess);
}

export function loadUserQuizQuestionFeedback(userId: string, quizAttemptId: string, quizVersionQuestionId: string) {
  return clientSupabase
    .from('user_quiz_question_feedback')
    .select()
    .eq('user_id', userId)
    .eq('quiz_attempt_id', quizAttemptId)
    .eq('quiz_version_question_id', quizVersionQuestionId)
    .maybeSingle()
    .then(dataOnSuccess);
}

export function removeUserQuizQuestionFeedback(userId: string, quizAttemptId: string, quizVersionQuestionId: string) {
  return clientSupabase
    .from('user_quiz_question_feedback')
    .delete()
    .eq('user_id', userId)
    .eq('quiz_attempt_id', quizAttemptId)
    .eq('quiz_version_question_id', quizVersionQuestionId);
}
